<template>
  <div class="waste-result-display">
    <!-- Mode visualisation ludique (pour enfants) -->
    <div v-if="currentMode === 'display'" class="space-y-4">
      <div class="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-6 text-white shadow-lg text-center">
        <h2 class="text-2xl font-bold mb-2">🗑️ Gaspillage évalué</h2>
        <p class="text-sm opacity-90">{{ yesterdayLabel }}</p>
      </div>

      <!-- Poubelle visuelle animée -->
      <div class="bg-white rounded-xl p-6 shadow-sm text-center">
        <div class="relative inline-block">
          <div class="text-9xl">🗑️</div>
          <div
            class="absolute bottom-0 left-0 right-0 bg-red-500 rounded-b-3xl transition-all"
            :style="{ height: wasteLevel + '%', opacity: 0.7 }"
          ></div>
        </div>
        <p class="text-5xl font-bold text-red-600 mt-4">{{ totalWeightKg.toFixed(2) }} kg</p>
        <p class="text-lg text-gray-600 mt-2">jetés à la poubelle ! 😢</p>
      </div>

      <!-- Équivalences concrètes -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3 text-center">C'est comme si on jetait...</h3>
        <div class="space-y-3">
          <div
            v-for="equiv in equivalences"
            :key="equiv.id"
            class="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border-2 border-blue-200"
          >
            <div class="flex items-center gap-4">
              <span class="text-5xl">{{ equiv.icon }}</span>
              <div class="flex-1">
                <p class="text-3xl font-bold text-blue-600">{{ equiv.quantity }}</p>
                <p class="text-sm text-gray-700">{{ equiv.label }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Détail par catégorie (uniquement si données disponibles) -->
      <div v-if="categoriesWithWaste.length > 0" class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3 text-center">📊 Détail du gaspillage</h3>
        <div class="space-y-2">
          <div
            v-for="cat in categoriesWithWaste"
            :key="cat.id"
            class="flex items-center justify-between bg-orange-50 rounded-lg p-3"
          >
            <span class="text-2xl">{{ cat.icon }}</span>
            <span class="font-semibold text-gray-700">{{ cat.name }}</span>
            <span class="text-xl font-bold text-orange-600">{{ cat.weight_kg.toFixed(2) }} kg</span>
          </div>
        </div>
      </div>

      <!-- Challenge & encouragement -->
      <div class="bg-gradient-to-r from-green-400 to-green-500 rounded-xl p-6 text-white shadow-lg text-center">
        <p class="text-3xl mb-3">🎯</p>
        <p class="text-xl font-bold mb-2">Notre défi</p>
        <p class="text-lg">Essayons de faire mieux aujourd'hui !</p>
        <p class="text-sm opacity-90 mt-2">Objectif : -{{ challengeReduction }}kg</p>
      </div>

      <!-- Comparaison avec avant-hier -->
      <div v-if="beforeYesterdayWeightKg !== null" class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3 text-center">📅 Par rapport à avant-hier</h3>
        <div class="flex items-center justify-center gap-8">
          <div class="text-center">
            <p class="text-sm text-gray-600">Avant-hier</p>
            <p class="text-3xl font-bold text-gray-500">{{ beforeYesterdayWeightKg.toFixed(2) }} kg</p>
          </div>
          <div class="text-5xl">
            {{ totalWeightKg < beforeYesterdayWeightKg ? "😊" : "😕" }}
          </div>
          <div class="text-center">
            <p class="text-sm text-gray-600">Hier</p>
            <p
              class="text-3xl font-bold"
              :class="totalWeightKg < beforeYesterdayWeightKg ? 'text-green-600' : 'text-red-600'"
            >
              {{ totalWeightKg.toFixed(2) }} kg
            </p>
          </div>
        </div>
        <div class="text-center mt-4">
          <p v-if="totalWeightKg < beforeYesterdayWeightKg" class="text-green-600 font-bold text-lg">
            👏 Bravo ! {{ (beforeYesterdayWeightKg - totalWeightKg).toFixed(1) }}kg de moins qu'avant-hier !
          </p>
          <p v-else class="text-orange-600 font-semibold">On peut faire mieux aujourd'hui !</p>
        </div>
      </div>

      <!-- Actions -->
      <div class="grid grid-cols-2 gap-3">
        <button
          @click="currentMode = 'poster'"
          class="bg-blue-500 hover:bg-blue-600 text-white rounded-lg p-4 font-semibold shadow-md transition"
        >
          🖨️ Afficher l'affiche
        </button>
        <button
          @click="$emit('back')"
          v-if="!todaySaved"
          class="bg-gray-500 hover:bg-gray-600 text-white rounded-lg p-4 font-semibold shadow-md transition"
        >
          Saisie du jour
        </button>
      </div>
    </div>

    <!-- Mode affiche imprimable -->
    <div v-if="currentMode === 'poster'" class="bg-white p-8 space-y-6">
      <div class="text-center border-8 border-red-500 rounded-2xl p-6 bg-red-50">
        <h1 class="text-5xl font-bold text-red-600 mb-4">🗑️ GASPILLAGE</h1>
        <p class="text-3xl mb-2">{{ yesterdayLabel }}</p>
        <p class="text-7xl font-bold text-red-700 my-6">{{ totalWeightKg.toFixed(2) }} KG</p>
        <p class="text-2xl">jetés à la poubelle</p>
      </div>

      <div class="grid grid-cols-2 gap-6">
        <div
          v-for="equiv in equivalences.slice(0, 2)"
          :key="equiv.id"
          class="border-4 border-blue-400 rounded-xl p-6 text-center bg-blue-50"
        >
          <p class="text-6xl mb-3">{{ equiv.icon }}</p>
          <p class="text-4xl font-bold text-blue-600">{{ equiv.quantity }}</p>
          <p class="text-xl mt-2">{{ equiv.label }}</p>
        </div>
      </div>

      <div class="bg-green-100 border-4 border-green-500 rounded-xl p-6 text-center">
        <p class="text-4xl mb-3">🎯</p>
        <p class="text-3xl font-bold text-green-700">NOTRE DÉFI</p>
        <p class="text-2xl mt-3">Essayons de faire mieux aujourd'hui !</p>
      </div>

      <div class="flex gap-3 justify-center">
        <button @click="printPoster" class="bg-blue-500 text-white px-6 py-3 rounded-lg font-bold text-lg">
          🖨️ Imprimer
        </button>
        <button @click="currentMode = 'display'" class="bg-gray-400 text-white px-6 py-3 rounded-lg font-bold text-lg">
          ← Retour
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WasteResultDisplay",
  props: {
    // Poids total gaspillé hier (J-1), en kg
    totalWeightKg: {
      type: Number,
      required: true,
    },
    // Détail par catégorie (optionnel — renseigné pour les versions medium et barquette)
    // Format : [{ id, name, icon, weight_kg }]
    categories: {
      type: Array,
      default: () => [],
    },
    // Poids mesuré avant-hier (J-2), pour la comparaison — null si non disponible
    beforeYesterdayWeightKg: {
      type: Number,
      default: null,
    },
    // Objectif de réduction affiché dans le challenge
    challengeReduction: {
      type: Number,
      default: 1.0,
    },
    // Mode initial : 'display' (visualisation) ou 'poster' (affiche imprimable)
    initialMode: {
      type: String,
      default: "display",
      validator: (v) => ["display", "poster"].includes(v),
    },
    // Vrai si la saisie du jour a déjà été enregistrée
    todaySaved: {
      type: Boolean,
      default: false,
    },
    // Date sélectionnée dans le header (J), format YYYY-MM-DD — null = aujourd'hui
    mealDate: {
      type: String,
      default: null,
    },
  },
  emits: ["back"],
  data() {
    return {
      currentMode: this.initialMode,
    }
  },
  computed: {
    yesterdayLabel() {
      const ref = this.mealDate ? new Date(this.mealDate) : new Date()
      ref.setDate(ref.getDate() - 1)
      return ref.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })
    },
    wasteLevel() {
      return Math.min((this.totalWeightKg / 10) * 100, 100)
    },
    equivalences() {
      const w = this.totalWeightKg
      return [
        { id: 1, icon: "🍔", quantity: Math.round(w / 0.2), label: "hamburgers" },
        { id: 2, icon: "🍎", quantity: Math.round(w / 0.15), label: "pommes" },
        { id: 3, icon: "🥖", quantity: Math.round(w / 0.25), label: "baguettes" },
        {
          id: 4,
          icon: "💧",
          quantity: Math.round(w * 1000),
          label: "litres d'eau gaspillés pour produire ces aliments",
        },
      ]
    },
    categoriesWithWaste() {
      return this.categories.filter((c) => (c.weight_kg || 0) > 0)
    },
  },
  methods: {
    printPoster() {
      window.print()
    },
  },
}
</script>

<style scoped>
.space-y-4 > * + * {
  margin-top: 1rem;
}
.space-y-3 > * + * {
  margin-top: 0.75rem;
}
.space-y-2 > * + * {
  margin-top: 0.5rem;
}
.space-y-6 > * + * {
  margin-top: 1.5rem;
}
.gap-3 {
  gap: 0.75rem;
}
.gap-4 {
  gap: 1rem;
}
.gap-6 {
  gap: 1.5rem;
}
.gap-8 {
  gap: 2rem;
}
.mt-2 {
  margin-top: 0.5rem;
}
.mt-3 {
  margin-top: 0.75rem;
}
.mt-4 {
  margin-top: 1rem;
}
.mb-2 {
  margin-bottom: 0.5rem;
}
.mb-3 {
  margin-bottom: 0.75rem;
}
.mb-4 {
  margin-bottom: 1rem;
}
.my-6 {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
}
.grid {
  display: grid;
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.flex {
  display: flex;
}
.flex-1 {
  flex: 1 1 0%;
}
.items-center {
  align-items: center;
}
.justify-center {
  justify-content: center;
}
.justify-between {
  justify-content: space-between;
}
.relative {
  position: relative;
}
.absolute {
  position: absolute;
}
.inline-block {
  display: inline-block;
}
.bottom-0 {
  bottom: 0;
}
.left-0 {
  left: 0;
}
.right-0 {
  right: 0;
}
.text-center {
  text-align: center;
}
.bg-gradient-to-br {
  background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
}
.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}
.from-orange-500 {
  --tw-gradient-from: #f97316;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
.to-red-500 {
  --tw-gradient-to: #ef4444;
}
.from-blue-50 {
  --tw-gradient-from: #eff6ff;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
.to-purple-50 {
  --tw-gradient-to: #faf5ff;
}
.from-green-400 {
  --tw-gradient-from: #4ade80;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
.to-green-500 {
  --tw-gradient-to: #22c55e;
}
.rounded-xl {
  border-radius: 0.75rem;
}
.rounded-2xl {
  border-radius: 1rem;
}
.rounded-lg {
  border-radius: 0.5rem;
}
.rounded-b-3xl {
  border-bottom-left-radius: 1.5rem;
  border-bottom-right-radius: 1.5rem;
}
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}
.shadow-md {
  box-shadow: 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
.shadow-lg {
  box-shadow: 0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.border-2 {
  border-width: 2px;
}
.border-4 {
  border-width: 4px;
}
.border-8 {
  border-width: 8px;
}
.border-blue-200 {
  border-color: #bfdbfe;
}
.border-blue-400 {
  border-color: #60a5fa;
}
.border-red-500 {
  border-color: #ef4444;
}
.border-green-500 {
  border-color: #22c55e;
}
.bg-white {
  background-color: #ffffff;
}
.bg-red-50 {
  background-color: #fef2f2;
}
.bg-red-500 {
  background-color: #ef4444;
}
.bg-orange-50 {
  background-color: #fff7ed;
}
.bg-blue-50 {
  background-color: #eff6ff;
}
.bg-blue-500 {
  background-color: #3b82f6;
}
.hover\:bg-blue-600:hover {
  background-color: #2563eb;
}
.bg-green-100 {
  background-color: #dcfce7;
}
.bg-gray-400 {
  background-color: #9ca3af;
}
.bg-gray-500 {
  background-color: #6b7280;
}
.hover\:bg-gray-600:hover {
  background-color: #4b5563;
}
.text-white {
  color: #ffffff;
}
.text-blue-600 {
  color: #2563eb;
}
.text-red-600 {
  color: #dc2626;
}
.text-red-700 {
  color: #b91c1c;
}
.text-green-600 {
  color: #16a34a;
}
.text-green-700 {
  color: #15803d;
}
.text-orange-600 {
  color: #ea580c;
}
.text-gray-500 {
  color: #6b7280;
}
.text-gray-600 {
  color: #4b5563;
}
.text-gray-700 {
  color: #374151;
}
.text-gray-800 {
  color: #1f2937;
}
.font-bold {
  font-weight: 700;
}
.font-semibold {
  font-weight: 600;
}
.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}
.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem;
}
.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem;
}
.text-3xl {
  font-size: 1.875rem;
  line-height: 2.25rem;
}
.text-4xl {
  font-size: 2.25rem;
  line-height: 2.5rem;
}
.text-5xl {
  font-size: 3rem;
  line-height: 1;
}
.text-6xl {
  font-size: 3.75rem;
  line-height: 1;
}
.text-7xl {
  font-size: 4.5rem;
  line-height: 1;
}
.text-9xl {
  font-size: 8rem;
  line-height: 1;
}
.p-3 {
  padding: 0.75rem;
}
.p-4 {
  padding: 1rem;
}
.p-6 {
  padding: 1.5rem;
}
.p-8 {
  padding: 2rem;
}
.px-6 {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
.py-3 {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}
.opacity-70 {
  opacity: 0.7;
}
.opacity-90 {
  opacity: 0.9;
}
.transition {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
button {
  cursor: pointer;
  border: none;
  background: none;
}
button:hover {
  opacity: 0.9;
}
@media print {
  button {
    display: none;
  }
}
</style>
